/**
 * DevisForm — saisie d'un devis, partagée entre :
 *   - la page /facturation/devis/nouveau (création, `asPage`) — même ergonomie
 *     que « Nouvel OR » et « Nouvelle facture » (iter262)
 *   - la dialog d'édition d'un devis existant (depuis la fiche devis)
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VehiculePicker from "@/components/VehiculePicker";
import OrReferencesBlock from "@/components/or/OrReferencesBlock";
import LignesEditor from "@/components/LignesEditor";
import QuickClientDialog from "@/components/QuickClientDialog";
import QuickVehiculeDialog from "@/components/QuickVehiculeDialog";
import VehiculeCard from "@/components/VehiculeCard";
import ClientSelectedCard, { clientLabel } from "@/components/ClientSelectedCard";

// Enveloppe des blocs : Card en page (comme OR / facture), simple div en dialog.
// Défini hors du composant : sinon React voit un nouveau type à chaque frappe,
// démonte tout le formulaire et le champ en cours de saisie perd le focus.
const Section = ({ asPage, children, testId }) => (asPage
  ? <Card className="p-5 mb-4 border-gray-200" data-testid={testId}>{children}</Card>
  : <div data-testid={testId}>{children}</div>);

export default function DevisForm({
  open = true,
  onClose,
  onCreated,
  editDevis = null,
  asPage = false,
}) {
  const isEdit = !!editDevis;
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [client, setClient] = useState(null);          // objet client complet (tél, e-mail, adresse)
  const [clientId, setClientId] = useState("");
  const [selectedClientLabel, setSelectedClientLabel] = useState("");
  const [vehicules, setVehicules] = useState([]);
  const [vehiculeId, setVehiculeId] = useState("");
  const [showQuickVehicule, setShowQuickVehicule] = useState(false);
  const [titre, setTitre] = useState("");
  const [conditions, setConditions] = useState("");
  const [enteteLibre, setEnteteLibre] = useState("");
  const [references, setReferences] = useState([]);
  const [piedLibre, setPiedLibre] = useState("");
  const [validite, setValidite] = useState(30);
  const [lignes, setLignes] = useState([]);
  const [rabaisGlobal, setRabaisGlobal] = useState({ pct: 0, chf: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [showQuickClient, setShowQuickClient] = useState(false);

  // Détection des modifs (pour avertissement à la fermeture) — désactivé en mode édition
  const isDirty = !isEdit && !!(clientId || titre || conditions || enteteLibre || piedLibre
    || lignes.length > 0 || rabaisGlobal.pct > 0 || rabaisGlobal.chf > 0);

  const handleCloseAttempt = () => {
    if (isDirty) {
      if (!window.confirm("Vous avez des modifications non enregistrées.\n\nAbandonner ces modifications ?")) return;
    }
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    if (isEdit && editDevis) {
      // Mode édition : préremplit depuis le devis existant
      setClientId(editDevis.client_id || "");
      setSelectedClientLabel(editDevis.contact_label || "");
      setClient(editDevis.client_detail || null);
      setVehiculeId(editDevis.vehicule_id || "");
      setTitre(editDevis.titre || "");
      setConditions(editDevis.conditions || "");
      setEnteteLibre(editDevis.entete_libre || "");
      setReferences(editDevis.references || []);
      setPiedLibre(editDevis.pied_libre || "");
      setValidite(editDevis.validite_jours || 30);
      const lignesPre = (editDevis.lignes || []).map((l, i) => ({
        _localId: `edit-${i}-${Date.now()}`,
        libelle: l.libelle || "",
        quantite: l.quantite ?? 1,
        prix_unitaire_ht: l.prix_unitaire_ht ?? 0,
        tva_taux: l.tva_taux ?? 8.1,
        unite: l.unite || "",
        rabais_pct: l.rabais_pct || 0,
        rabais_chf: l.rabais_chf || 0,
        type_ligne: l.type_ligne || "libre",
        article_id: l.article_id || null,
        categorie_compta: l.categorie_compta || null,
      }));
      setLignes(lignesPre);
      setRabaisGlobal({
        pct: editDevis.rabais_global_pct || 0,
        chf: editDevis.rabais_global_chf || 0,
      });
      setClientSearch("");
    }
  }, [open, isEdit, editDevis]);

  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClients([]); return; }
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: clientSearch, limit: 10 } })
        .then(({ data }) => setClients(data.items || data || []))
        .catch(() => setClients([]));
    }, 200);
    return () => clearTimeout(t);
  }, [clientSearch]);

  useEffect(() => {
    if (!clientId) { setVehicules([]); return; }
    api.get(`/clients/${clientId}`)
      .then(({ data }) => {
        setVehicules(data.vehicules || []);
        const { vehicules: _v, ...fiche } = data || {};
        setClient((prev) => ({ ...fiche, ...(prev || {}) }));
      })
      .catch(() => setVehicules([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const clearClient = () => {
    setClientId(""); setClient(null); setSelectedClientLabel(""); setVehiculeId(""); setVehicules([]);
  };

  const submit = async () => {
    if (!clientId) { toast.error("Sélectionnez un client"); return false; }
    const valides = lignes.filter((l) => l.libelle && l.libelle.trim());
    if (valides.length === 0) { toast.error("Ajoutez au moins une ligne"); return false; }
    setSubmitting(true);
    try {
      const lignesPayload = valides.map((l) => ({
        libelle: l.libelle.trim(),
        quantite: parseFloat(l.quantite) || 1,
        prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
        tva_taux: parseFloat(l.tva_taux) || 8.1,
        unite: l.unite || "",
        rabais_pct: parseFloat(l.rabais_pct) || 0,
        rabais_chf: parseFloat(l.rabais_chf) || 0,
        type_ligne: l.type_ligne || "libre",
        article_id: l.article_id || null,
        categorie_compta: l.categorie_compta || null,
      }));

      if (isEdit) {
        await api.patch(`/devis/${editDevis.id}`, {
          titre: titre || "",
          lignes: lignesPayload,
          conditions: conditions || "",
          entete_libre: enteteLibre || "",
          pied_libre: piedLibre || "",
          references,
          rabais_global_pct: parseFloat(rabaisGlobal.pct) || 0,
          rabais_global_chf: parseFloat(rabaisGlobal.chf) || 0,
        });
        toast.success(`Devis ${editDevis.numero} modifié`);
        onCreated?.();
      } else {
        const { data } = await api.post("/devis", {
          client_id: clientId,
          vehicule_id: vehiculeId || null,
          titre: titre || "",
          lignes: lignesPayload,
          conditions: conditions || "",
          validite_jours: parseInt(validite, 10) || 30,
          rabais_global_pct: parseFloat(rabaisGlobal.pct) || 0,
          rabais_global_chf: parseFloat(rabaisGlobal.chf) || 0,
          entete_libre: enteteLibre || "",
          pied_libre: piedLibre || "",
          references,
        });
        toast.success(`Devis ${data.numero} créé`);
        onCreated?.(data);
      }
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
      return false;
    } finally { setSubmitting(false); }
  };

  const handleQuickClientCreated = (newClient) => {
    setClient(newClient);
    setClientId(newClient.id);
    setSelectedClientLabel(clientLabel(newClient));
    setShowQuickClient(false);
  };

  const clientSection = (
    <Section asPage={asPage} testId="devis-form-client-section">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Client *</Label>
        {!clientId && !isEdit && (
          <Button type="button" size="sm" variant="outline" onClick={() => setShowQuickClient(true)} data-testid="devis-new-client-btn">
            <Plus className="w-3 h-3 mr-1" /> Nouveau client
          </Button>
        )}
      </div>
      {isEdit ? (
        client ? (
          <ClientSelectedCard client={client} testIdPrefix="devis-client" />
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-sm font-semibold text-gray-800">
            {selectedClientLabel || "Client"}
            <span className="ml-2 text-[10px] text-gray-500 font-normal">(non modifiable ici)</span>
          </div>
        )
      ) : !clientId ? (
        <>
          <Input
            placeholder="Rechercher (nom, prénom, raison sociale, plaque…)"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            data-testid="devis-client-search"
          />
          {clientSearch.length >= 2 && clients.length === 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Aucun client trouvé. <button type="button" onClick={() => setShowQuickClient(true)} className="text-[#52a675] hover:underline font-semibold">Créer un nouveau client</button>
            </p>
          )}
          {clients.length > 0 && (
            <div className="border border-gray-200 rounded-lg mt-2 max-h-40 overflow-y-auto">
              {clients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setClient(c);
                    setClientId(c.id);
                    setSelectedClientLabel(clientLabel(c));
                    setClientSearch("");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                  data-testid={`devis-client-pick-${c.id}`}
                >
                  <div className="font-semibold">{clientLabel(c)}</div>
                  <div className="text-[10px] text-gray-500">{c.tel_principal || c.tel_secondaire || c.email || "—"}</div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <ClientSelectedCard client={client || { nom: selectedClientLabel }} onClear={clearClient} testIdPrefix="devis-client" />
      )}
    </Section>
  );

  const vehiculeSection = clientId && !isEdit ? (
    <Section asPage={asPage} testId="devis-vehicule-section">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
          Véhicule <span className="text-gray-400 normal-case">(optionnel)</span>
        </Label>
        <Button size="sm" variant="outline" type="button" onClick={() => setShowQuickVehicule(true)} className="h-7 text-xs" data-testid="devis-quick-vehicule-btn">
          <Plus className="w-3 h-3 mr-1" /> Ajouter
        </Button>
      </div>
      {vehicules.length > 0 ? (
        <VehiculePicker
          vehicules={vehicules}
          value={vehiculeId}
          onChange={setVehiculeId}
          allowNone
          noneLabel="Aucun véhicule"
          testId="devis-vehicule"
          showDetails
          onVehiculeUpdated={(v) => setVehicules((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...v } : x)))}
        />
      ) : (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-500 text-center">
          Aucun véhicule pour ce client. Clique « Ajouter » pour en créer un.
        </div>
      )}
    </Section>
  ) : null;

  const vehiculeEditSection = isEdit && editDevis?.vehicule_label ? (
    <Section asPage={asPage} testId="devis-vehicule-edit-section">
      <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Véhicule</Label>
      {editDevis.vehicule_detail ? (
        <VehiculeCard vehicule={editDevis.vehicule_detail} />
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-sm text-gray-700">
          {editDevis.vehicule_label}
          <span className="ml-2 text-[10px] text-gray-500">(non modifiable ici)</span>
        </div>
      )}
    </Section>
  ) : null;

  const metaSection = (
    <Section asPage={asPage} testId="devis-meta-section">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className={isEdit ? "sm:col-span-3" : "sm:col-span-2"}>
          <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Titre / Objet</Label>
          <Input
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Ex : Révision 60'000 km, Carrosserie aile arrière…"
            maxLength={150}
            data-testid="devis-titre"
          />
        </div>
        {!isEdit && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Validité (jours)</Label>
            <Input
              type="number" min="1" max="365"
              value={validite}
              onChange={(e) => setValidite(e.target.value)}
              data-testid="devis-validite"
            />
          </div>
        )}
      </div>
    </Section>
  );

  const refsSection = (
    <Section asPage={asPage} testId="devis-references-section">
      <OrReferencesBlock value={references} onChange={setReferences} />
    </Section>
  );

  const lignesSection = (
    <Section asPage={asPage} testId="devis-lignes-section">
      <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Lignes *</Label>
      <LignesEditor
        lignes={lignes}
        setLignes={setLignes}
        rabaisGlobal={rabaisGlobal}
        setRabaisGlobal={setRabaisGlobal}
        tvaDefaut={8.1}
      />
    </Section>
  );

  const textesSection = (
    <Section asPage={asPage} testId="devis-textes-section">
      <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">
        Texte de pied de page <span className="text-gray-400 normal-case">(bas du PDF, avant les conditions générales)</span>
      </Label>
      <Textarea
        value={piedLibre}
        onChange={(e) => setPiedLibre(e.target.value)}
        rows={2}
        maxLength={1000}
        placeholder="Ex : Délais de livraison : 5-7 jours ouvrés. Garantie pièces : 2 ans."
        data-testid="devis-pied-libre"
      />
      <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 mt-4 block">Conditions / Notes au client</Label>
      <Textarea
        value={conditions}
        onChange={(e) => setConditions(e.target.value)}
        rows={2}
        maxLength={500}
        placeholder="Conditions de paiement, exclusions, etc."
        data-testid="devis-conditions"
      />
    </Section>
  );

  const sections = (
    <>
      {clientSection}
      {vehiculeSection}
      {vehiculeEditSection}
      {metaSection}
      {refsSection}
      {lignesSection}
      {textesSection}
    </>
  );

  const submitButton = (
    <Button onClick={submit} disabled={submitting || !clientId} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="devis-submit">
      {submitting
        ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
        : (isEdit ? <Pencil className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />)}
      {isEdit ? "Enregistrer les modifications" : "Enregistrer le devis"}
    </Button>
  );

  const quickDialogs = (
    <>
      <QuickClientDialog
        open={showQuickClient}
        onClose={() => setShowQuickClient(false)}
        onCreated={handleQuickClientCreated}
        prefillSearch={clientSearch}
      />
      <QuickVehiculeDialog
        open={showQuickVehicule}
        onClose={() => setShowQuickVehicule(false)}
        clientId={clientId}
        clientNom={selectedClientLabel}
        onCreated={async (v) => {
          try {
            const { data } = await api.get(`/clients/${clientId}`);
            setVehicules(data.vehicules || []);
            setVehiculeId(v.id);
          } catch (e) { /* ignore */ }
          setShowQuickVehicule(false);
        }}
      />
    </>
  );

  if (asPage) {
    return (
      <div data-testid="devis-form-page">
        {sections}
        <div className="flex items-center justify-end gap-2 flex-wrap pb-8">
          <Button variant="outline" onClick={handleCloseAttempt} data-testid="devis-cancel">
            <X className="w-4 h-4 mr-2" /> Annuler
          </Button>
          {submitButton}
        </div>
        {quickDialogs}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseAttempt()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (isDirty) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isDirty) { e.preventDefault(); handleCloseAttempt(); } }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier le devis ${editDevis.numero}` : "Nouveau devis"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Modification des lignes, rabais et textes du devis. Le client et le véhicule ne peuvent pas être changés ici."
              : "Devis autonome avec catalogue articles, rabais et calcul automatique."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">{sections}</div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseAttempt}>Annuler</Button>
          {submitButton}
        </DialogFooter>
      </DialogContent>
      {quickDialogs}
    </Dialog>
  );
}
